import chroma from 'chroma-js';
import { type TextObject as MapTextObject, TRANSPARENT } from '../../../../common/types';
import CanvasObject from '../CanvasObject';
import type SurfaceBase from '../SurfaceBase';
import type { Point } from '../SurfaceBase';

export default class TextObject extends CanvasObject {
  text: MapTextObject;

  private point: Point;
  private baseZoom: number;
  private baseSize: number;
  private fontSize: number;
  private lineHeight: number;
  private bboxWidth: number;
  private lines: string[];

  constructor(id: string, surface: SurfaceBase, text: MapTextObject) {
    super(id, surface);
    this.text = text;
    this.point = this.surface.toScreenCoords({
      lng: text.geometry.coordinates[0],
      lat: text.geometry.coordinates[1],
    });
    this.baseZoom = this.text.properties.createdAtZoom;
    this.baseSize = this.text.properties.fontSize;
    this.fontSize = this.baseSize * 2 ** (this.surface.map.getZoom() - this.baseZoom);
    this.lineHeight = this.fontSize + 5;
    this.bboxWidth = 0;
    this.lines = ['Empty Text'];
  }

  refreshScreenCoords(): void {
    this.point = this.surface.toScreenCoords({
      lng: this.text.geometry.coordinates[0],
      lat: this.text.geometry.coordinates[1],
    });
  }

  drawHitArea(ctx: OffscreenCanvasRenderingContext2D, color: string): void {
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    if (this.fontSize < 5) {
      // can't really see, fine to skip rendering
      return;
    }

    const x = this.point.x;
    const y = this.point.y;

    const bboxPadding = this.fontSize;
    const translateX = x + this.bboxWidth / 2;
    const translateY = y + (this.lines.length / 2) * this.lineHeight - this.fontSize;

    ctx.save();
    ctx.resetTransform();

    ctx.translate(translateX, translateY);
    ctx.rotate(this.text.properties.rotation);
    ctx.translate(-translateX, -translateY);

    ctx.lineWidth = 5;
    ctx.fillRect(
      this.point.x - bboxPadding / 2,
      this.point.y - bboxPadding,
      this.bboxWidth + bboxPadding,
      this.lines.length * (this.fontSize + 5) + bboxPadding / 2,
    );
    ctx.restore();
  }

  render(ctx: CanvasRenderingContext2D): void {
    const { description, properties } = this.text;

    this.baseZoom = properties.createdAtZoom;
    this.baseSize = properties.fontSize;

    // exponential interpolation with base 2
    this.fontSize = this.baseSize * 2 ** (this.surface.map.getZoom() - this.baseZoom);

    if (this.fontSize < 5) {
      // can't really see, fine to skip rendering
      return;
    }

    const { x, y } = this.point;
    ctx.font = `bold ${this.fontSize}px sans-serif`;
    ctx.textBaseline = 'alphabetic';

    this.lines = description.trim() === '' ? ['Empty Text'] : description.split('\n');
    this.lineHeight = this.fontSize + 5;

    // let bboxWidth = 0;
    this.bboxWidth = 0;
    for (let i = 0; i < this.lines.length; i++) {
      const textMetrics = ctx.measureText(this.lines[i]);
      this.bboxWidth = Math.max(this.bboxWidth, textMetrics.width);
    }

    const bboxPadding = this.fontSize;
    const translateX = x + this.bboxWidth / 2;
    const translateY = y + (this.lines.length / 2) * this.lineHeight - this.fontSize;

    ctx.save();
    this.surface.resetCanvasTransform(ctx);

    ctx.translate(translateX, translateY);
    ctx.rotate(this.text.properties.rotation);
    ctx.translate(-translateX, -translateY);

    if (properties.backgroundColor !== TRANSPARENT) {
      ctx.lineWidth = 1;
      ctx.strokeStyle = properties.color;
      ctx.beginPath();
      ctx.roundRect(
        x - bboxPadding / 2,
        y - bboxPadding,
        this.bboxWidth + bboxPadding,
        this.lines.length * (this.fontSize + 5) + bboxPadding / 2,
        5,
      );
      ctx.fillStyle = chroma(properties.backgroundColor).alpha(0.75).hex();
      ctx.fill();
    }

    // draw text
    ctx.beginPath();
    ctx.lineWidth = 0.5;
    ctx.strokeStyle = properties.backgroundColor === TRANSPARENT ? 'white' : properties.color;
    ctx.fillStyle = properties.color;

    for (let i = 0; i < this.lines.length; i++) {
      ctx.fillText(this.lines[i], x, y + i * this.lineHeight);
      ctx.strokeText(this.lines[i], x, y + i * this.lineHeight);
    }

    ctx.restore();
  }
}
